import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { WebSocketService } from '../websocket/websocket.service';
import { TranscriptMetadata } from '../../models/documentResponse';
import { TranscriptMetadataService } from '../transcript-metadata/transcript-metadata.service';
import { from } from 'rxjs';
import { switchMap, filter, map, bufferTime } from 'rxjs/operators';
import { TranscriptType } from '../../constants';

export interface TranscriptStatusUpdate {
  transcriptId: string;
  status: string;
  fileName?: string;
  confidence_score?: string;
  timestamp?: string;
  typeId?: number;
}

export interface TranscriptAssignmentUpdate {
  transcriptId: string;
  assignedTo: string;
  assignedToUserId: string;
  fileName?: string;
}

export interface TranscriptUnassignmentUpdate {
  transcriptId: string;
  previouslyAssignedTo: string;
  fileName?: string;
}

export interface TranscriptFieldUpdate {
  transcriptId: string;
  fileName?: string;
  firstName?: string;
  lastName?: string;
  confidence_score?: string;
  updatedFields: string[];
}

export interface TranscriptCreatedUpdate {
  transcriptId: string;
  documentId: string;
  fileName?: string;
  status: string;
  createdAt?: string;
  typeId?: number;
}

export interface TranscriptDeletedUpdate {
  transcriptId: string;
  fileName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TranscriptRealtimeService implements OnDestroy {
  private wsService = inject(WebSocketService);
  private transcriptMetadataService = inject(TranscriptMetadataService);
  private allowedTranscriptType: TranscriptType | null = null;

  private transcriptsSubject = new BehaviorSubject<TranscriptMetadata[]>([]);
  public transcripts$ = this.transcriptsSubject.asObservable();

  private pendingUpdates = new Subject<{
    transcriptId: string;
    updates: Partial<TranscriptMetadata>;
  }>();
  private subscriptions = new Subscription();
  private isInitialized = false;

  /**
   * Initialize WebSocket connection and set up subscriptions
   * Call this when the transcript feature is loaded
   */
  connect(): void {
    if (this.isInitialized) return;

    this.wsService.connect();
    this.setupBatchedUpdates();
    this.setupRealtimeUpdates();
    this.isInitialized = true;
  }

  /**
   * Disconnect WebSocket
   * Call this when leaving the transcript feature
   */
  disconnect(): void {
    this.wsService.disconnect();
    this.isInitialized = false;
  }

  /**
   * Listen for transcript status updates
   * @returns Observable of transcript status updates
   */
  onTranscriptStatusUpdate(): Observable<TranscriptStatusUpdate> {
    return this.wsService.on<TranscriptStatusUpdate>('transcript:status');
  }

  /**
   * Listen for transcript assignment updates
   * @returns Observable of transcript assignment updates
   */
  onTranscriptAssigned(): Observable<TranscriptAssignmentUpdate> {
    return this.wsService.on<TranscriptAssignmentUpdate>('transcript:assigned');
  }

  /**
   * Listen for transcript unassignment updates
   * @returns Observable of transcript unassignment updates
   */
  onTranscriptUnassigned(): Observable<TranscriptUnassignmentUpdate> {
    return this.wsService.on<TranscriptUnassignmentUpdate>('transcript:unassigned');
  }

  /**
   * Listen for transcript field updates (name, confidence, etc.)
   * @returns Observable of transcript field updates
   */
  onTranscriptFieldUpdate(): Observable<TranscriptFieldUpdate> {
    return this.wsService.on<TranscriptFieldUpdate>('transcript:updated');
  }

  /**
   * Listen for transcript creation events
   * @returns Observable of transcript creation updates
   */
  onTranscriptCreated(): Observable<TranscriptCreatedUpdate> {
    return this.wsService.on<TranscriptCreatedUpdate>('transcript:created');
  }

  /**
   * Listen for transcript deletion events
   * @returns Observable of transcript deletion updates
   */
  onTranscriptDeleted(): Observable<TranscriptDeletedUpdate> {
    return this.wsService.on<TranscriptDeletedUpdate>('transcript:deleted');
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.wsService.isConnected();
  }

  /**
   * Set the transcripts state
   * @param transcripts Array of transcript metadata
   */
  setTranscripts(transcripts: TranscriptMetadata[]): void {
    this.transcriptsSubject.next(transcripts);
  }

  /**
   * Restrict real-time transcript creation events to a single transcript type
   * @param type The transcript type the current user is allowed to see, or null for all
   */
  setAllowedTranscriptType(type: TranscriptType | null): void {
    this.allowedTranscriptType = type;
  }

  /**
   * Get current transcripts value
   */
  getTranscripts(): TranscriptMetadata[] {
    return this.transcriptsSubject.value;
  }

  /**
   * Get a single transcript by ID as an Observable
   * @param transcriptId The ID of the transcript to retrieve
   */
  getTranscriptById$(transcriptId: string): Observable<TranscriptMetadata | undefined> {
    return this.transcripts$.pipe(
      map(transcripts => transcripts.find(t => t.transId === transcriptId))
    );
  }

  /**
   * Queue an update for a single transcript. Updates are batched
   * and flushed together to avoid excessive re-renders.
   * @param transcriptId The ID of the transcript to update
   * @param updates Partial transcript metadata fields to update
   */
  updateTranscript(transcriptId: string, updates: Partial<TranscriptMetadata>): void {
    const current = this.transcriptsSubject.value;
    const index = current.findIndex(t => t.transId === transcriptId);
    if (index !== -1) {
      const next = [...current];
      next[index] = { ...next[index], ...updates };
      this.transcriptsSubject.next(next);
    }
    this.pendingUpdates.next({ transcriptId, updates });
  }

  /**
   * Sets up the batched update pipeline. Collects individual
   * updateTranscript calls over a 150ms window and applies
   * them in a single pass, emitting one new array.
   */
  private setupBatchedUpdates(): void {
    this.subscriptions.add(
      this.pendingUpdates
        .pipe(
          bufferTime(150),
          filter(batch => batch.length > 0)
        )
        .subscribe(batch => {
          const current = [...this.transcriptsSubject.value];

          const updateMap = new Map<string, Partial<TranscriptMetadata>>();
          for (const { transcriptId, updates } of batch) {
            const existing = updateMap.get(transcriptId) || {};
            updateMap.set(transcriptId, { ...existing, ...updates });
          }

          for (let i = 0; i < current.length; i++) {
            const pending = updateMap.get(current[i].transId);
            if (pending) {
              current[i] = { ...current[i], ...pending };
            }
          }

          this.transcriptsSubject.next(current);
        })
    );
  }

  /**
   * Remove a transcript from the state
   * @param transcriptId The ID of the transcript to remove
   */
  removeTranscript(transcriptId: string): void {
    const currentTranscripts = this.transcriptsSubject.value;
    const updatedTranscripts = currentTranscripts.filter(t => t.transId !== transcriptId);
    this.transcriptsSubject.next(updatedTranscripts);
  }

  /**
   * Set up WebSocket subscriptions for real-time updates
   */
  private setupRealtimeUpdates(): void {
    this.subscriptions.add(
      this.onTranscriptStatusUpdate()
        .pipe(switchMap(update => from(this.handleRealtimeUpdate(update))))
        .subscribe()
    );

    this.subscriptions.add(
      this.onTranscriptAssigned().subscribe(update => {
        this.handleAssignmentUpdate(update);
      })
    );

    this.subscriptions.add(
      this.onTranscriptUnassigned().subscribe(update => {
        this.handleUnassignmentUpdate(update);
      })
    );

    this.subscriptions.add(
      this.onTranscriptFieldUpdate().subscribe(update => {
        this.handleFieldUpdate(update);
      })
    );

    this.subscriptions.add(
      this.onTranscriptCreated().subscribe(update => {
        this.handleTranscriptCreated(update);
      })
    );

    this.subscriptions.add(
      this.onTranscriptDeleted().subscribe(update => {
        this.handleTranscriptDeleted(update);
      })
    );
  }

  /**
   * Handles real-time transcript status updates from WebSocket
   * @param update - The transcript status update data
   */
  private async handleRealtimeUpdate(update: TranscriptStatusUpdate): Promise<void> {
    const transcript = this.getTranscripts().find(t => t.transId === update.transcriptId);

    if (!transcript) {
      return;
    }

    if (update.status === 'Ready for Export' || update.status === 'Needs Review') {
      try {
        const summary = await this.transcriptMetadataService.getTranscriptSummary(
          update.transcriptId
        );

        this.updateTranscript(update.transcriptId, {
          extractionStatus: update.status,
          student_name: `${summary.firstName || ''} ${summary.lastName || ''}`.trim(),
          confidence_score: summary.confidenceScore
            ? `${Math.round(summary.confidenceScore * 100)}%`
            : update.confidence_score || transcript.confidence_score,
          typeId: update.typeId ?? transcript.typeId,
          typeMismatch: summary.typeMismatch ?? transcript.typeMismatch
        });
      } catch (error) {
        console.error('Failed to fetch transcript summary:', error);
        this.updateTranscript(update.transcriptId, {
          extractionStatus: update.status,
          confidence_score: update.confidence_score || transcript.confidence_score,
          typeId: update.typeId ?? transcript.typeId
        });
      }
    } else {
      this.updateTranscript(update.transcriptId, {
        extractionStatus: update.status,
        confidence_score: update.confidence_score || transcript.confidence_score,
        typeId: update.typeId ?? transcript.typeId
      });
    }
  }

  /**
   * Handles real-time transcript assignment updates from WebSocket
   * @param update - The transcript assignment update data
   */
  private handleAssignmentUpdate(update: TranscriptAssignmentUpdate): void {
    const transcript = this.getTranscripts().find(t => t.transId === update.transcriptId);

    if (!transcript) {
      return;
    }

    this.updateTranscript(update.transcriptId, {
      lockedByUser: update.assignedTo
    });
  }

  /**
   * Handles real-time transcript unassignment updates from WebSocket
   * @param update - The transcript unassignment update data
   */
  private handleUnassignmentUpdate(update: TranscriptUnassignmentUpdate): void {
    const transcript = this.getTranscripts().find(t => t.transId === update.transcriptId);

    if (!transcript) {
      return;
    }

    this.updateTranscript(update.transcriptId, {
      lockedByUser: null
    });
  }

  /**
   * Handles real-time transcript field updates from WebSocket
   * @param update - The transcript field update data
   */
  private handleFieldUpdate(update: TranscriptFieldUpdate): void {
    const transcript = this.getTranscripts().find(t => t.transId === update.transcriptId);

    if (!transcript) {
      return;
    }

    this.updateTranscript(update.transcriptId, {
      student_name:
        update.firstName && update.lastName
          ? `${update.firstName} ${update.lastName}`.trim()
          : transcript.student_name,
      confidence_score: update.confidence_score || transcript.confidence_score
    });
  }

  /**
   * Handles real-time transcript creation events from WebSocket
   * @param update - The transcript creation update data
   */
  private handleTranscriptCreated(update: TranscriptCreatedUpdate): void {
    if (
      this.allowedTranscriptType !== null &&
      update.typeId != null &&
      update.typeId !== this.allowedTranscriptType
    ) {
      return;
    }

    const exists = this.getTranscripts().some(t => t.transId === update.transcriptId);
    if (exists) {
      return;
    }

    const currentTranscripts = this.getTranscripts();
    const newTranscript: TranscriptMetadata = {
      transId: update.transcriptId,
      documentId: update.documentId,
      fileName:
        update.fileName && update.fileName !== 'pending_upload' ? update.fileName : 'Unknown',
      extractionStatus: update.status,
      typeId: update.typeId,
      createdAt: update.createdAt || new Date().toISOString(),
      updatedAt: update.createdAt || new Date().toISOString(),
      contentUri: '',
      transcriptData: undefined,
      student_name: '',
      confidence_score: '',
      lockedByUser: null
    };

    this.transcriptsSubject.next([...currentTranscripts, newTranscript]);
  }

  /**
   * Handles real-time transcript deletion events from WebSocket
   * @param update - The transcript deletion update data
   */
  private handleTranscriptDeleted(update: TranscriptDeletedUpdate): void {
    const transcript = this.getTranscripts().find(t => t.transId === update.transcriptId);

    if (!transcript) {
      return;
    }

    this.removeTranscript(update.transcriptId);
  }

  /**
   * Clean up WebSocket subscriptions and disconnect
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.disconnect();
  }
}
